import { useNavigate } from 'react-router-dom';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { type CheckedState } from '@radix-ui/react-checkbox';
import { useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { MARKET_FUNCTION_SUPPORT } from '@/pages/Market/_constants/const';

import { Button } from '@/components/atoms/PcButton';
import { Card } from '@/components/atoms/Card';
import Input from '@/components/atoms/Input';
import TextField from '@/components/molecules/TextField';
import { Dialog, DialogContent } from '@/components/molecules/Dialog';

import InquiryButton from '@/components/organisms/InquiryButton';
import { CPLAT_API_ROUTES, usePost } from '@/api';
import { useAppSelector } from '@/redux/store';
import { ROUTES } from '@/routes';
import { MARKET_TEXTBOX_OPTIONAL_LIST } from '@/pages/Market/_constants/const';
import PwSeeIcon from '@/assets/icons/PwSeeIcon.svg?react';
import PwHideIcon from '@/assets/icons/PwHideIcon.svg?react';
import SecondAuthContainer from './SecondAuthContainer';
import { useLog } from '@/hooks/V3/useLog';
import ProvideFeatures from './ProvideFeatures';
import { getMarketRegisterErrorMessage } from './get-market-register-error-message';

// type RegisterMarketBody = {
//   MarketName: string;
//   MarketId: string;
//   MarketPassword: string;
//   VendorId: string;
//   AccessKey: string;
//   SecretKey: string;
//   AuthCode: string;
//   AgreementList: {
//     Key: string;
//     GuideMessage: string;
//     ReferenceMessage: string;
//     ReferenceUrl: string;
//     IsInputMandatory: boolean;
//     values: boolean;
//   }[];
// };

type TermsFormData = {
  Key: string;
  GuideMessage: string;
  ReferenceMessage: string;
  ReferenceUrl: string;
  IsInputMandatory: boolean;
  values: CheckedState;
};

type FormData = {
  [key: string]: string;
} & {
  AgreementList: TermsFormData[];
};

type MarketRegisterContainerProps = {
  marketTextboxOptionalList: {
    Key: string;
    PlaceHolder: string;
    HasToBeEncrypted: boolean;
  }[];
  marketAuthCheckOptionalList: MarketAuthCheckOptional[];
  marketNameEnglish: string;
  marketName: string;
};

const DEFAULT_MARKET_INFO = {
  MarketName: '',
  MarketId: '',
  MarketPassword: '',
  VendorId: '',
  AccessKey: '',
  SecretKey: '',
  AuthCode: '',
  AgreementList: [],
};

const MarketRegisterContainer = ({
  marketTextboxOptionalList,
  marketAuthCheckOptionalList,
  marketName,
  marketNameEnglish,
}: MarketRegisterContainerProps) => {
  const { register, watch, handleSubmit, setValue, setError, formState } = useForm<FormData>({
    // @ts-ignore
    defaultValues: {
      ...marketTextboxOptionalList.reduce(
        (acc, { Key }) => ({ ...acc, [Key]: '' }),
        {} as Omit<FormData, 'AgreementList'>,
      ),
      AgreementList: marketAuthCheckOptionalList.map((item) => ({
        ...item,
        IsInputMandatory: true,
        values: true,
      })),
    },
  });

  // dialog
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

  const formValues = watch();
  const { errors } = formState;

  const inputValues = Object.values(formValues).filter((value) => typeof value === 'string');

  const isDisable =
    inputValues.every((value) => value !== '') &&
    formValues.AgreementList.every(({ values, IsInputMandatory }) => IsInputMandatory && values)
      ? false
      : true;

  const user = useAppSelector((state) => state.user);
  const { CplatToken, UserNo } = user;
  const { setUserEventHistory } = useLog(UserNo);

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // 입력값 숨기기 or 보이기
  const [hideList, setHideList] = useState(
    marketTextboxOptionalList
      .filter((item) => item.HasToBeEncrypted)
      .reduce(
        (acc, { Key }) => ({
          ...acc,
          [Key]: true,
        }),
        {} as { [key: string]: boolean },
      ),
  );

  // 2차 인증이 필요한 경우에, response로 받아온 휴대폰 번호에 대한 메시지
  const [secondAuthPhoneNumber, setSecondAuthPhoneNumber] = useState<string>('');
  // 남은 시간초
  const [seconds, setSeconds] = useState<number>(180);
  // 카운트다운 타이머 활성화 여부
  const [isActive, setIsActive] = useState<boolean>(false);

  // 2차 인증에 필요한 유효시간 3분 카운트 다운
  useEffect(() => {
    // 2차 인증 api 호출해서 타이머 활성화되었을 때 카운트 다운 시작
    if (seconds > 0 && isActive) {
      const timerId = setInterval(() => {
        setSeconds((prevSeconds) => prevSeconds - 1);
      }, 1000);

      return () => clearInterval(timerId);
    }
  }, [seconds, isActive]);

  const { mutate: marketRegister, isPending } = usePost(CPLAT_API_ROUTES.registerMarketAccount, {});

  // 연동 동의 핸들러
  const handleCheck = (key: string, checked: CheckedState) =>
    setValue(
      'AgreementList',
      formValues.AgreementList.map((agreement) =>
        agreement.Key === key ? { ...agreement, values: checked } : agreement,
      ),
    );

  // 마켓등록 핸들러
  const onSubmit: SubmitHandler<FormData> = (formData, e) => {
    // TODO 동의받는 로직은 따로 구현해야 한다.
    e?.preventDefault();

    const body = {
      CplatToken,
      MarketName: marketNameEnglish,
      MarketLoginInfo: JSON.stringify({
        ...DEFAULT_MARKET_INFO,
        MarketName: marketNameEnglish,
        ...formData,
      }),
    };

    marketRegister(body, {
      onSuccess(res) {
        const { code, message } = res.data as CplatApiResponse<string>;

        if (code === '200') {
          navigate(`${ROUTES.PcMarketConnectinoComplete}/${marketNameEnglish}`);
          queryClient.refetchQueries({
            queryKey: [CPLAT_API_ROUTES.getRegisteredMarketInfo],
          });
          setUserEventHistory({
            eventName: 'connect_market',
            eventValue: marketNameEnglish,
          });
          return;
        }

        if (code === '610') {
          setError('root', {
            message: getMarketRegisterErrorMessage({ code, message, marketNameEnglish }),
          });
          return;
        }

        // 다른 셀러에 의해 이미 등록된 마켓일 경우
        if (code === '611') {
          setError('root', { message });
          return;
        }

        // 2차 인증이 필요한 경우 (e.g. 롯데온)
        if (code === '615') {
          setSecondAuthPhoneNumber(message);

          // 남은 시간 카운트 활성화
          setIsActive(true);
          // 잔여 시간 3분 설정
          setSeconds(180);
          return;
        }

        if (code === '771') {
          setError('root', { message }); // "토스 쇼핑 MID 오류" 메시지 표시
          return;
        }

        setError('root', { message });
      },
    });
  };
  const marketIconUrl = MARKET_FUNCTION_SUPPORT[marketNameEnglish]?.marketIconUrl;

  return (
    <>
      <div className="flex max-w-[700px]  flex-col gap-[20px] rounded-[20px] pl-[6px]">
        {/* 
        1) marketNameEnglish === 'lotteon' 이면서
        2) 인증 api 호출 후에 노출됨 */}
        {marketNameEnglish === 'lotteon' && isActive && (
          <SecondAuthContainer
            where={'ADD_MARKET'}
            seconds={seconds}
            secondAuthPhoneNumber={secondAuthPhoneNumber}
            marketNameEnglish={marketNameEnglish}
            marketName={marketName}
            marketId={formValues.MarketId}
          />
        )}
        <Card className="px-[20px] py-[24px]">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              {marketIconUrl && (
                <img src={marketIconUrl} alt={`${marketNameEnglish} 아이콘`} className="h-6 w-6" />
              )}
              <h2 className="text-gr-900 Body1S16">{`${marketName} 연동하기`}</h2>
            </div>
            <form>
              <div className="relative flex flex-col gap-4">
                {marketTextboxOptionalList.map(({ HasToBeEncrypted, Key, PlaceHolder }, i) => {
                  // 라벨(제목) 매핑: 별칭이 항상 있으므로 안전하게 사용
                  // const label = MARKET_TEXTBOX_OPTIONAL_LIST[Key]?.title || Key;

                  // PlaceHolder 값에 따라 라벨을 지정
                  let label;
                  if (PlaceHolder === 'API 키를 입력하세요.') {
                    label = 'API 키';
                  } else if (PlaceHolder === '이메일 주소를 입력하세요.') {
                    label = '이메일';
                  } else if (PlaceHolder === '셀러 ID를 입력해 주세요.') {
                    label = '셀러 ID';
                  } else if (PlaceHolder === 'OTP 인증코드를 입력해 주세요') {
                    label = 'OTP 인증코드';
                  } else if (PlaceHolder === 'Access key를 입력하세요.') {
                    label = 'AccessKey';
                  } else if (PlaceHolder === 'Secret key를 입력하세요.') {
                    label = 'SecretKey';
                  } else {
                    label = MARKET_TEXTBOX_OPTIONAL_LIST[Key]?.title || Key;
                  }
                  return (
                    <div key={Key} className={`flex items-center gap-4`}>
                      <label
                        className={`text-gr-800  Body2M16 ${
                          marketNameEnglish === 'pallang'
                            ? 'w-[110px]'
                            : marketNameEnglish === 'coupang'
                            ? 'w-[100px]'
                            : 'w-[80px]'
                        }`}
                      >
                        {label}
                        <span className="ml-1 text-red-400">*</span>
                      </label>
                      <div className="flex-1">
                        {HasToBeEncrypted ? (
                          <TextField
                            {...register(Key)}
                            placeholder={PlaceHolder}
                            type={hideList[Key] ? 'password' : 'text'}
                            variant="register"
                            className="h-[40px] Body5M14"
                            InputProps={{
                              endAdornment: hideList[Key] ? (
                                <PwHideIcon
                                  onClick={() =>
                                    setHideList({ ...hideList, [Key]: !hideList[Key] })
                                  }
                                  className="cursor-pointer"
                                />
                              ) : (
                                <PwSeeIcon
                                  onClick={() =>
                                    setHideList({ ...hideList, [Key]: !hideList[Key] })
                                  }
                                  className="cursor-pointer"
                                />
                              ),
                            }}
                          />
                        ) : (
                          <Input
                            {...register(Key)}
                            placeholder={PlaceHolder}
                            variant="register"
                            className="h-[40px] w-full Body5M14"
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
                {errors.root?.message && (
                  <p className="text-red-600 Caption1M12">{errors.root.message}</p>
                )}
              </div>
              <div className="mt-4 flex flex-col gap-4 ">
                {formValues.AgreementList.map(({ Key, values }) => (
                  <div key={Key} className="flex items-center justify-center space-x-[8px]">
                    <input
                      type="checkbox"
                      className="h-4 w-4 cursor-pointer rounded-[2px] border-gr-300 accent-point-500"
                      // value={values ? 'on' : 'off'}
                      checked={values === true}
                      onChange={(e) => handleCheck(Key, e.target.checked)}
                    />

                    <p className=" text-gr-700 Body5M14">[필수] 계정 연동 권한 동의</p>
                    <a
                      className="cursor-pointer align-bottom text-gr-600 underline Body5M14"
                      href="https://sellerbox.notion.site/db71e932abda4ab1bc23cb51113533e9"
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      전문보기
                    </a>
                  </div>
                ))}
                <div className="flex items-center justify-center">
                  <Button
                    type="submit"
                    className=" h-[44px] w-[300px] rounded-[16px] Body5M14"
                    disabled={isDisable || isPending}
                    onClick={handleSubmit(onSubmit)}
                  >
                    {'연동하기'}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </Card>
        <ProvideFeatures marketNameEnglish={marketNameEnglish} />
        <InquiryButton className="" />
      </div>
      {/* <Dialog open={isTermsModalOpen} onOpenChange={() => setIsTermsModalOpen(!isTermsModalOpen)}>
        <DialogContent className="flex h-[500px] !w-[800px] flex-col items-center !p-0 text-center">
          <div
            className="flex w-full cursor-pointer justify-end p-[20px]"
            onClick={() => setIsTermsModalOpen(false)}
          >
            <X />
          </div>
          <iframe
            src="https://sellerbox.notion.site/db71e932abda4ab1bc23cb51113533e9"
            className="h-[100%] w-[100%] rounded-[16px]"
          />
        </DialogContent>
      </Dialog> */}
    </>
  );
};

export default MarketRegisterContainer;
